<template>
  <div
    class="field has-addons is-marginless"
    v-on-clickaway="closeActive"
  >
    <div
      v-if="hasPrefix"
      class="control"
    >
      <div
        :class="{'is-active': isPrefixActive}"
        class="dropdown"
      >
        <div class="dropdown-trigger">
          <button
            class="button"
            @click="isPrefixActive = !isPrefixActive"
          >
            <span>{{ prefixIndicator }}</span>
            <span class="icon is-small"><i class="fa fa-angle-down" /></span>
          </button>
        </div>
        <div class="dropdown-menu">
          <div class="dropdown-content">
            <div
              v-for="(value, index) in filter.prefixes"
              :key="index"
              class="dropdown-item"
            >
              <div
                data-testid="category-dropdown-item"
                class="field range-prefix-dropdown-item"
                @click="changePrefix(value)"
              >
                <input
                  type="checkbox"
                  :checked="value === prefix"
                  class="is-checkradio"
                >
                <label>{{ value }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="control">
      <input
        :id="`${filter.field}`"
        autocomplete="off"
        :value="selected"
        :placeholder="filter.name"
        class="input"
        type="number"
        @input="onInput"
        @focus="isEditing = true"
        @blur="onBlur"
      >
    </div>
    <div
      v-if="hasSuffix"
      class="control"
    >
      <div
        :class="{'is-active': isSuffixActive}"
        class="dropdown"
      >
        <div class="dropdown-trigger">
          <button
            class="button"
            @click="isSuffixActive = !isSuffixActive"
          >
            <span>{{ suffixIndicator }}</span>
            <span class="icon is-small">
              <i class="fa fa-angle-down" />
            </span>
          </button>
        </div>
        <div class="dropdown-menu">
          <div class="dropdown-content">
            <div
              v-for="(value, index) in filter.suffixes"
              :key="index"
              class="dropdown-item"
            >
              <div
                class="field range-suffix-dropdown-item"
                @click="changeSuffix(value)"
              >
                <input
                  type="checkbox"
                  :checked="value === suffix"
                  class="is-checkradio"
                >
                <label>{{ value }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="control">
      <a
        data-testid="remove-filter-button"
        class="button remove-filter-button"
        @click="removeFilter"
      >
        <span class="icon is-small">
          <i class="fa fa-times" />
        </span>
      </a>
    </div>
  </div>
</template>

<script>
import { directive as onClickaway } from 'vue3-click-away'
import debounce from 'lodash.debounce'

export default {
  name: 'RangeFilter',
  props: {
    filter: {
      type: Object,
      required: true
    },
    selectedValues: {
      type: Array,
      default: () => []
    }
  },
  directives: {
    onClickaway
  },
  data () {
    return {
      selected: '',
      isEditing: false,
      prefix: undefined,
      suffix: undefined,
      isPrefixActive: false,
      isSuffixActive: false
    }
  },
  watch: {
    selectedValues () {
      this.updateValues()
    }
  },
  computed: {
    hasPrefix () {
      return this.filter && this.filter.prefixes && this.filter.prefixes.length > 0
    },
    hasSuffix () {
      return this.filter && this.filter.suffixes && this.filter.suffixes.length > 0
    },
    prefixIndicator () {
      if (this.prefix) {
        return this.prefix
      }
      return this.$i18n.t('any')
    },
    suffixIndicator () {
      if (this.suffix) {
        return this.suffix
      }
      return this.$i18n.t('any')
    }
  },
  methods: {
    onInput (e) {
      this.selected = e.target.value
      // change() reads `this.selected` live when the debounce actually
      // fires, and calling a debounced fn resets its timer. So if the
      // user clears the field and keeps typing within the debounce
      // window, this naturally coalesces into a single commit of the
      // final value - an empty value only ever gets committed if the
      // field is genuinely left empty for the full debounce window.
      this.debouncedChange()
    },
    onBlur () {
      this.isEditing = false
      // Commit whatever the user actually left in the field right away,
      // instead of leaving a stale debounce pending. Don't call
      // updateValues() here - the `selectedValues` prop echo from this
      // flush hasn't propagated back down yet, so reading it now would
      // just clobber what we're committing with the old value. The
      // `watch: { selectedValues }` below will reconcile once it arrives.
      this.debouncedChange.flush()
    },
    changePrefix (value) {
      if (this.prefix !== value) {
        this.prefix = value
        this.change()
      }
    },
    changeSuffix (value) {
      if (this.suffix !== value) {
        this.suffix = value
        this.change()
      }
    },
    change () {
      this.$emit('change', [{
        value: this.selected,
        prefix: this.prefix,
        suffix: this.suffix
      }])
    },
    removeFilter () {
      this.debouncedChange.cancel()
      this.$emit('remove')
    },
    updateValues () {
      // Never let a prop update (e.g. an echo of our own debounced emit
      // arriving late) clobber what the user is actively typing.
      if (this.isEditing) return

      const item = this.selectedValues.length ? this.selectedValues[0] : undefined
      if (item) {
        this.selected = item.value.length ? item.value : ''

        if (this.hasPrefix) {
          this.prefix = item.prefix || this.filter.prefixes[0]
        }
        if (this.hasSuffix) {
          this.suffix = item.suffix || this.filter.suffixes[0]
        }
      } else {
        this.selected = ''
        this.prefix = this.hasPrefix ? this.filter.prefixes[0] : undefined
        this.suffix = this.hasSuffix ? this.filter.suffixes[0] : undefined
      }
    },
    closeActive () {
      this.isPrefixActive = false
      this.isSuffixActive = false
    }
  },
  created () {
    this.debouncedChange = debounce(this.change, 500)
  },
  mounted () {
    this.updateValues()
  },
  beforeUnmount () {
    this.debouncedChange.cancel()
  }
}
</script>

<style scoped>
input {
  max-width: 10rem;
}
.dropdown-content {
  width: max-content;
  max-height: 50vh;
  overflow: auto;
}
.button {
  padding-left: .5rem;
  padding-right: .5rem;
}
.button .icon:last-child:not(:first-child) {
  margin-left: 0;
}
</style>
